import { File, Virtualizer } from "@pierre/diffs/react";
import { useQuery } from "@tanstack/react-query";
import { FileTextIcon, LoaderCircleIcon } from "lucide-react";
import { useMemo, useRef } from "react";

import type { CommandId, EnvironmentId } from "@cmmd-forge/contracts";
import { resolveDiffThemeName } from "../../lib/diffRendering";
import { newCommandId } from "../../lib/utils";
import { runThreadExecutionIntentAction } from "../../lib/runThreadExecutionIntentAction";
import { readEnvironmentApi } from "../../environmentApi";
import { useTheme } from "../../hooks/useTheme";
import type { FileReadPolicy } from "../../rightPanelSurface";
import {
  FILE_PREVIEW_MAX_BYTES,
  clampRevealLine,
  countLines,
  filePreviewTitle,
  resolveFilePreviewBody,
} from "./filePreview.logic";

/**
 * Scoped to this panel so the viewer inherits Forge's surfaces rather than the
 * library's own chrome. Mirrors what `DiffPanel` does for the same reason: the
 * diff renderer ships a header and background of its own, and two different
 * file surfaces in one right panel would read as two different apps.
 */
const FILE_PREVIEW_UNSAFE_CSS = `
[data-file] {
  background: transparent;
}
[data-diffs-header] {
  display: none;
}
`;

export interface FilePreviewPanelProps {
  readonly environmentId: EnvironmentId;
  readonly projectId: number | null;
  /** The project root the path is relative to. */
  readonly cwd: string;
  readonly relativePath: string;
  readonly revealLine: number | null;
  /**
   * Bumped by the store on every open, including a repeat of the same line.
   *
   * Asking for the same line twice is a real request to scroll back to it, so
   * this is what makes the second request observable — equal state would make
   * the viewer ignore it.
   */
  readonly revealRequestId: number;
  readonly readPolicy: FileReadPolicy;
}

export function filePreviewQueryKey(input: {
  readonly cwd: string;
  readonly environmentId: EnvironmentId;
  readonly readPolicy: FilePreviewPanelProps["readPolicy"];
  readonly relativePath: string;
  readonly revealRequestId: number;
}) {
  return [
    "file-preview",
    input.environmentId,
    input.cwd,
    input.relativePath,
    input.readPolicy,
    input.revealRequestId,
  ] as const;
}

async function readFilePreview(input: {
  readonly environmentId: EnvironmentId;
  readonly projectId: number | null;
  readonly cwd: string;
  readonly relativePath: string;
  readonly revealRequestId: number;
  readonly readPolicy: FileReadPolicy;
  readonly intentIdentityRef: {
    current: { readonly key: string; readonly id: CommandId } | null;
  };
}) {
  if (input.readPolicy === "connected-only") {
    const api = readEnvironmentApi(input.environmentId);
    if (!api) throw new Error("Connect this environment to open the matched file.");
    return api.projects.readFile({
      cwd: input.cwd,
      relativePath: input.relativePath,
      maxBytes: FILE_PREVIEW_MAX_BYTES + 1,
    });
  }
  const intentKey = `${input.environmentId}:${input.cwd}:${input.relativePath}:${input.revealRequestId}`;
  if (input.intentIdentityRef.current?.key !== intentKey) {
    input.intentIdentityRef.current = { key: intentKey, id: newCommandId() };
  }
  const result = await runThreadExecutionIntentAction(
    input.environmentId,
    {
      action: "files",
      projectId: input.projectId,
      targetId: null,
      dispatchIdentity: input.intentIdentityRef.current.id,
    },
    {
      dispatch: async (api) => {
        if (!api.projects) throw new Error("This runtime cannot read files yet.");
        return api.projects.readFile({
          cwd: input.cwd,
          relativePath: input.relativePath,
          maxBytes: FILE_PREVIEW_MAX_BYTES + 1,
        });
      },
    },
  );
  input.intentIdentityRef.current = null;
  if (result.kind === "already-dispatched") {
    throw new Error("This file request already completed. Reopen the preview to read it again.");
  }
  return result.value;
}

/**
 * A read-only view of one workspace file.
 *
 * Ported from t3code's `FilePreviewPanel`, narrowed to reading. Theirs also
 * hosts an editor, an image preview and a browser-preview handoff; this covers
 * the surface the right panel actually opens today — a file, syntax
 * highlighted, scrolled to a line — and leaves the rest to follow rather than
 * shipping three half-built modes.
 *
 * Content is fetched as bytes and classified before decoding: a binary file
 * decoded as UTF-8 renders as pages of replacement characters, which reads as
 * corruption rather than as "not previewable".
 */
export function FilePreviewPanel({
  environmentId,
  projectId,
  cwd,
  relativePath,
  revealLine,
  revealRequestId,
  readPolicy,
}: FilePreviewPanelProps) {
  const { resolvedTheme } = useTheme();
  const intentIdentityRef = useRef<{ readonly key: string; readonly id: CommandId } | null>(null);

  const fileQuery = useQuery({
    queryKey: filePreviewQueryKey({
      environmentId,
      cwd,
      relativePath,
      revealRequestId,
      readPolicy,
    }),
    enabled: cwd.length > 0 && relativePath.length > 0,
    queryFn: () =>
      readFilePreview({
        environmentId,
        projectId,
        cwd,
        relativePath,
        revealRequestId,
        readPolicy,
        intentIdentityRef,
      }),
  });

  const body = useMemo(
    () => (fileQuery.data ? resolveFilePreviewBody(fileQuery.data) : null),
    [fileQuery.data],
  );

  const contents = body?.kind === "text" ? body.contents : null;
  const selectedLines = useMemo(() => {
    if (contents === null) return null;
    const line = clampRevealLine(revealLine, countLines(contents));
    return line === null ? null : { start: line, end: line };
  }, [contents, revealLine]);

  return (
    <FilePreviewContent
      environmentId={environmentId}
      cwd={cwd}
      relativePath={relativePath}
      revealRequestId={revealRequestId}
      resolvedTheme={resolvedTheme}
      body={body}
      contents={contents}
      selectedLines={selectedLines}
      isPending={fileQuery.isPending}
      error={fileQuery.error}
    />
  );
}

function FilePreviewContent(input: {
  readonly environmentId: EnvironmentId;
  readonly cwd: string;
  readonly relativePath: string;
  readonly revealRequestId: number;
  readonly resolvedTheme: "light" | "dark";
  readonly body: ReturnType<typeof resolveFilePreviewBody> | null;
  readonly contents: string | null;
  readonly selectedLines: { readonly start: number; readonly end: number } | null;
  readonly isPending: boolean;
  readonly error: unknown;
}) {
  if (input.isPending) return <FilePreviewNotice icon="spinner" text="Loading file…" />;

  if (input.error) {
    return <FilePreviewNotice text={filePreviewErrorMessage(input.error)} />;
  }

  if (input.body?.kind === "binary") {
    return (
      <FilePreviewNotice text={`${filePreviewTitle(input.relativePath)} is not a text file.`} />
    );
  }

  if (input.body?.kind === "too-large") {
    return (
      <FilePreviewNotice
        text={`${filePreviewTitle(input.relativePath)} is too large to preview (${formatBytes(input.body.bytes)}).`}
      />
    );
  }

  if (input.contents === null) return <FilePreviewNotice text="That file could not be read." />;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Virtualizer
        // Keyed by the reveal request so a repeated open of the same line
        // remounts the viewer and scrolls again, rather than being ignored as
        // unchanged state.
        key={`${input.relativePath}:${input.revealRequestId}`}
        className="min-h-0 flex-1 overflow-auto"
        config={{ overscrollSize: 600, intersectionObserverMargin: 1200 }}
      >
        <File
          file={{
            name: input.relativePath,
            contents: input.contents,
            cacheKey: `${input.environmentId}:${input.cwd}:${input.relativePath}:${input.contents.length}`,
          }}
          selectedLines={input.selectedLines}
          options={{
            theme: resolveDiffThemeName(input.resolvedTheme),
            themeType: input.resolvedTheme === "dark" ? "dark" : "light",
            unsafeCSS: FILE_PREVIEW_UNSAFE_CSS,
          }}
        />
      </Virtualizer>
    </div>
  );
}

function filePreviewErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "That file could not be read.";
}

/** Every non-content state, so they cannot drift apart from each other. */
function FilePreviewNotice({
  text,
  icon = "file",
}: {
  readonly text: string;
  readonly icon?: "file" | "spinner";
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-5 text-center">
      {icon === "spinner" ? (
        <LoaderCircleIcon className="size-5 animate-spin text-muted-foreground/40" />
      ) : (
        <FileTextIcon className="size-7 text-muted-foreground/25" />
      )}
      <p className="text-xs text-muted-foreground/70">{text}</p>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
